import { ReactNode, SyntheticEvent, useCallback, useEffect, useState } from 'react'
import ControlSize from '../../../../types/control-size'
import NumberFormat, { displayTextToNumber, numberToDisplayText } from '../../../../types/number-format'
import { Container, Input, Label } from './style'

type Props = {
    className?: string
    extension?: ReactNode
    format?: NumberFormat
    placeholder?: string
    size: ControlSize
    step?: number
    value?: number
    onChange?: (value: number) => void
}

const NumberInputLabel = ({
    className,
    extension,
    format,
    placeholder = '--',
    size,
    step = 1,
    value,
    onChange,
}: Props) => {
    const [text, setText] = useState<string>('')
    const [isFocused, setIsFocused] = useState<boolean>(false)

    const updateTextToValue = useCallback(() => {
        if (typeof value === 'number' && format) {
            setText(numberToDisplayText(value, format))
            return
        }

        setText(typeof value === 'number' ? `${value}` : '')
    }, [format, value])

    const onBlur = () => {
        const parsedText = parseFloat(text)

        if (!isNaN(parsedText)) {
            if (format) {
                const formattedValue = displayTextToNumber(text, format)
                onChange?.(formattedValue && !isNaN(formattedValue) ? formattedValue : parseFloat(text))
            } else {
                onChange?.(parseFloat(text))
            }
        } else {
            updateTextToValue()
        }

        setIsFocused(false)
    }

    const onFocus = (e: SyntheticEvent<HTMLInputElement, Event>) => {
        e.currentTarget.select()
        setIsFocused(true)
    }

    const onKeyDown = (e: SyntheticEvent<HTMLInputElement, KeyboardEvent>) => {
        const arrowStep = e.nativeEvent.shiftKey ? step * 10 : step

        switch (e.nativeEvent.key) {
            case 'Enter':
                onBlur()
                break
            case 'ArrowUp':
                onChange?.((value || 0) + arrowStep)
                break
            case 'ArrowDown':
                onChange?.((value || 0) - arrowStep)
                break
        }
    }

    const onInput = (e: SyntheticEvent<HTMLInputElement, Event>) => {
        setText(e.currentTarget.value)
    }

    useEffect(() => {
        updateTextToValue()
    }, [format, value])

    return (
        <Container className={className} controlSize={size}>
            <Label style={{ opacity: isFocused ? 0 : 1 }}>
                {text || placeholder}
                {text ? extension : null}
            </Label>
            <Input
                placeholder={placeholder}
                style={{ opacity: isFocused ? 1 : 0 }}
                type="text"
                value={text}
                onBlur={onBlur}
                onChange={onInput}
                onFocus={onFocus}
                onKeyDown={onKeyDown}
            />
        </Container>
    )
}

export default NumberInputLabel
